import { useRef, useState } from "react";
import { ArrowRight, Loader2, Monitor } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";
import { PEOPLE } from "@/lib/spaces";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AtomMark } from "@/components/AtomMark";

export default function Login() {
  const { login } = useAuth();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const pwRef = useRef(null);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await login(username, password);
    } catch (err) {
      setError(errMsg(err));
      setBusy(false);
    }
  };

  const pick = (name) => {
    setUsername(name);
    pwRef.current?.focus();
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-[1.1fr_1fr]">
      <BrandPanel />
      <div className="flex items-center px-6 py-16 sm:px-12 lg:px-20">
        <form onSubmit={submit} className="fade-up w-full max-w-sm" data-testid="login-form">
          <div className="mb-10 flex items-center gap-3 lg:hidden">
            <AtomMark size={36} />
            <span className="font-display text-xl font-bold">Atom IP</span>
          </div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Team sign in</p>
          <h1 className="mt-3 font-display text-4xl font-extrabold tracking-tight">Welcome back.</h1>
          <p className="mt-3 text-sm text-muted-foreground">Who's signing in?</p>
          <div className="mt-4 flex flex-wrap gap-2">
            {PEOPLE.map((p) => (
              <button
                key={p.key}
                type="button"
                data-testid={`login-preset-${p.key}`}
                onClick={() => pick(p.name)}
                className="rounded-full border px-4 py-1.5 text-sm font-semibold transition-colors hover:bg-white/5"
                style={username.toLowerCase() === p.key ? { borderColor: p.color, color: p.color, background: `${p.color}1a` } : {}}
              >
                {p.name}
              </button>
            ))}
            <button type="button" data-testid="login-preset-screen" onClick={() => pick("screen")}
              className={`flex items-center gap-1.5 rounded-full border border-dashed px-4 py-1.5 text-sm font-semibold transition-colors hover:bg-white/5 ${username.toLowerCase() === "screen" ? "border-indigo-400 bg-indigo-500/10 text-indigo-300" : ""}`}>
              <Monitor className="h-3.5 w-3.5" /> TV screen
            </button>
          </div>
          <div className="mt-8 space-y-5">
            <div className="space-y-2">
              <Label htmlFor="username">Name</Label>
              <Input id="username" data-testid="login-username-input" value={username} onChange={(e) => setUsername(e.target.value)} placeholder="e.g. Kyle" autoComplete="username" className="h-11" required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input id="password" ref={pwRef} type="password" data-testid="login-password-input" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" className="h-11" required />
            </div>
          </div>
          {error && <p className="mt-4 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300" data-testid="login-error">{error}</p>}
          <button type="submit" disabled={busy} data-testid="login-submit-button" className="group mt-8 flex h-12 w-full items-center justify-center gap-2 rounded-full bg-primary font-semibold text-primary-foreground transition-[background-color,opacity] hover:bg-white disabled:opacity-60">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Sign in <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></>}
          </button>
        </form>
      </div>
    </div>
  );
}

function BrandPanel() {
  return (
    <div className="relative hidden overflow-hidden border-r bg-[#0d1320] p-14 lg:flex lg:flex-col lg:justify-between">
      <div className="flex items-center gap-3">
        <AtomMark size={34} />
        <span className="font-display text-lg font-bold tracking-tight">Atom IP</span>
      </div>
      <div className="pointer-events-none absolute -right-40 top-1/2 -translate-y-1/2 opacity-60">
        <AtomMark size={620} stroke={0.6} />
      </div>
      <div className="relative max-w-md">
        <h2 className="font-display text-5xl font-extrabold leading-[1.05] tracking-tight">
          Reminders, projects &amp; the things we need to talk about.
        </h2>
        <p className="mt-6 text-base text-muted-foreground">One shared board for Josh, Kyle, Taya &amp; Ollie. Every reminder lands in your inbox and support's.</p>
        <div className="mt-10 flex gap-2">
          {PEOPLE.map((p) => <span key={p.key} className="h-1.5 w-10 rounded-full" style={{ background: p.color }} />)}
        </div>
      </div>
    </div>
  );
}
